import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Check, X as XIcon, Trash2, Star, ExternalLink, Loader2, Pencil, ShieldCheck, ShieldAlert,
} from "lucide-react";

const RIGHTS_LABEL = {
  UNKNOWN: "Inconnu",
  TO_VERIFY: "À vérifier",
  AUTHORIZED: "Autorisée",
  LICENSED: "Sous licence",
  INTERNAL: "Interne AITO",
  REJECTED: "Rejetée",
};

const RIGHTS_VARIANT = {
  UNKNOWN: "outline",
  TO_VERIFY: "outline",
  AUTHORIZED: "default",
  LICENSED: "default",
  INTERNAL: "secondary",
  REJECTED: "destructive",
};

const ROLE_LABEL = {
  cover: "Couverture",
  portrait: "Portrait",
  action: "Action",
  podium: "Podium",
  gallery: "Galerie",
  other: "Autre",
};

/**
 * Tuile média MemoryBook (Admin).
 * Affiche l'aperçu, les droits, et les actions (validate, reject, edit, delete).
 * Le PDF ne consomme JAMAIS l'URL externe : après validation, une copie
 * interne est téléchargée et référencée via `internal_url`.
 */
export function MemoryBookMediaTile({ media, onChange, onDelete, disabled = false }) {
  const [busy, setBusy] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const status = media.rights_status || "UNKNOWN";
  const canValidate = status === "TO_VERIFY" || status === "UNKNOWN";
  const canReject = status !== "REJECTED";
  const previewUrl = media.internal_url || media.original_image_url;

  return (
    <Card className="p-3 flex flex-col gap-2" data-testid={`mb-media-tile-${media.id}`}>
      <div className="relative aspect-[4/3] rounded overflow-hidden bg-muted flex items-center justify-center">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={media.alt || media.caption || ""}
            className="w-full h-full object-cover object-top"
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
          />
        ) : (
          <span className="text-xs text-muted-foreground italic">Pas d'aperçu</span>
        )}
        <div className="absolute top-1 left-1 flex gap-1">
          <Badge variant={RIGHTS_VARIANT[status] || "outline"} data-testid={`mb-media-rights-${media.id}`}>
            {RIGHTS_LABEL[status] || status}
          </Badge>
          {media.role === "cover" && (
            <Badge className="bg-[hsl(var(--aito-gold))] text-black">Couverture</Badge>
          )}
        </div>
      </div>

      <div className="text-xs space-y-0.5">
        <div className="font-medium truncate">{media.caption || media.credit || "Sans légende"}</div>
        <div className="text-muted-foreground truncate">
          Rôle: {ROLE_LABEL[media.role] || media.role || "—"} · {media.source_type}
        </div>
        {media.credit && (
          <div className="text-muted-foreground truncate">Crédit : {media.credit}</div>
        )}
        {media.license && (
          <div className="text-muted-foreground truncate">Licence : {media.license}</div>
        )}
        {media.source_page_url && (
          <a
            href={media.source_page_url}
            target="_blank"
            rel="noreferrer"
            className="text-[hsl(var(--aito-lagoon))] hover:underline inline-flex items-center gap-0.5"
          >
            Source <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 pt-1 border-t">
        {canValidate && (
          <ValidateMediaButton
            media={media}
            busy={busy === "validate"}
            disabled={disabled}
            onValidated={(updated) => { setBusy(null); onChange?.(updated); }}
            onBusy={(v) => setBusy(v ? "validate" : null)}
          />
        )}
        {canReject && (
          <RejectMediaButton
            media={media}
            busy={busy === "reject"}
            disabled={disabled}
            onRejected={(updated) => { setBusy(null); onChange?.(updated); }}
            onBusy={(v) => setBusy(v ? "reject" : null)}
          />
        )}
        <Button
          variant="ghost" size="sm"
          onClick={() => setEditOpen(true)}
          data-testid={`mb-media-edit-${media.id}`}
          disabled={disabled}
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Éditer
        </Button>
        <Button
          variant="ghost" size="sm"
          onClick={() => onDelete?.(media)}
          disabled={disabled}
          data-testid={`mb-media-delete-${media.id}`}
          className="text-red-600 hover:text-red-700"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <EditMediaDialog
        media={media}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={(updated) => { setEditOpen(false); onChange?.(updated); }}
      />
    </Card>
  );
}

function ValidateMediaButton({ media, busy, disabled, onValidated, onBusy }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    rights_status: media.source_type === "INTERNAL_DB" ? "INTERNAL" : "AUTHORIZED",
    credit: media.credit || "",
    license: media.license || "",
    role: media.role || "gallery",
  });
  const [guessBusy, setGuessBusy] = useState(false);
  const [guessInfo, setGuessInfo] = useState(null);
  const api = require("@/lib/api").default;

  const runGuess = async () => {
    setGuessBusy(true);
    try {
      const { data } = await api.post(`/admin/memory-books/medias/${media.id}/guess-attribution`);
      setGuessInfo(data);
      setForm((f) => ({
        ...f,
        credit: f.credit || data.suggested_credit || "",
        license: f.license || data.suggested_license || "",
      }));
      require("sonner").toast.success("Attribution devinée depuis la page source.");
    } catch (e) {
      require("sonner").toast.error(e?.response?.data?.detail || "Échec de la devinette d'attribution");
    } finally {
      setGuessBusy(false);
    }
  };

  const submit = async () => {
    onBusy(true);
    try {
      const { data } = await api.post(`/admin/memory-books/medias/${media.id}/validate`, form);
      require("sonner").toast.success("Média validé — copie interne figée.");
      setOpen(false);
      onValidated(data);
    } catch (e) {
      require("sonner").toast.error(e?.response?.data?.detail || "Échec validation");
      onBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm" variant="default" disabled={disabled || busy}
        onClick={() => setOpen(true)}
        data-testid={`mb-media-validate-${media.id}`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
        Valider
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Valider et figer ce média</DialogTitle>
            <DialogDescription>
              La copie interne sera téléchargée et conservée dans AITO.PF pour garantir
              la reproductibilité du livret. L'image externe originale ne sera plus
              relue au rendu du PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {media.source_page_url && (
              <div className="flex items-center justify-between gap-2 rounded border bg-muted/40 p-2">
                <div className="text-[11px] text-muted-foreground truncate flex-1">
                  Page source : <span className="font-mono">{media.source_page_url}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runGuess}
                  disabled={guessBusy}
                  data-testid={`mb-guess-attribution-${media.id}`}
                >
                  {guessBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                  Deviner l'attribution
                </Button>
              </div>
            )}
            {guessInfo && (
              <div className="rounded border border-[hsl(var(--aito-lagoon))]/40 bg-[hsl(var(--aito-lagoon))]/5 p-2 text-[11px] space-y-0.5">
                <div className="font-semibold text-[hsl(var(--aito-lagoon))]">Suggestions détectées :</div>
                {guessInfo.attribution?.photographer && <div>Photographe : <strong>{guessInfo.attribution.photographer}</strong></div>}
                {guessInfo.attribution?.author && <div>Auteur : <strong>{guessInfo.attribution.author}</strong></div>}
                {guessInfo.attribution?.organization && <div>Publication : <strong>{guessInfo.attribution.organization}</strong></div>}
                {guessInfo.suggested_license && <div>Licence probable : <strong>{guessInfo.suggested_license}</strong></div>}
                {!guessInfo.attribution?.photographer && !guessInfo.attribution?.author && !guessInfo.suggested_license && (
                  <div className="italic text-muted-foreground">Aucune info fiable détectée — remplis manuellement.</div>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Statut des droits *</Label>
              <Select value={form.rights_status} onValueChange={(v) => setForm({ ...form, rights_status: v })}>
                <SelectTrigger data-testid="mb-validate-rights"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AUTHORIZED">Autorisée (accord écrit ou verbal)</SelectItem>
                  <SelectItem value="LICENSED">Sous licence (CC, éditeur…)</SelectItem>
                  <SelectItem value="INTERNAL">Interne AITO.PF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Crédit à afficher</Label>
              <Input
                data-testid="mb-validate-credit"
                value={form.credit}
                onChange={(e) => setForm({ ...form, credit: e.target.value })}
                placeholder="© Jean Dupont / Tahiti Sport Presse"
              />
            </div>
            <div>
              <Label className="text-xs">Licence / mention légale</Label>
              <Input
                data-testid="mb-validate-license"
                value={form.license}
                onChange={(e) => setForm({ ...form, license: e.target.value })}
                placeholder="Tous droits réservés · CC-BY-SA-4.0…"
              />
            </div>
            <div>
              <Label className="text-xs">Rôle dans le livret</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="mb-validate-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={busy} data-testid="mb-validate-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Valider et figer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RejectMediaButton({ media, busy, disabled, onRejected, onBusy }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const api = require("@/lib/api").default;

  const submit = async () => {
    onBusy(true);
    try {
      const { data } = await api.post(`/admin/memory-books/medias/${media.id}/reject`, { reason });
      require("sonner").toast.success("Média marqué comme rejeté.");
      setOpen(false);
      onRejected(data);
    } catch (e) {
      require("sonner").toast.error(e?.response?.data?.detail || "Échec");
      onBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost" size="sm"
        disabled={disabled || busy}
        onClick={() => setOpen(true)}
        data-testid={`mb-media-reject-${media.id}`}
        className="text-amber-700 hover:text-amber-800"
      >
        <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Rejeter
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeter ce média</DialogTitle>
            <DialogDescription>Précisez la raison (droits non obtenus, qualité insuffisante…).</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="mb-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={submit} disabled={busy} data-testid="mb-reject-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Rejeter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditMediaDialog({ media, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    role: media.role || "gallery",
    caption: media.caption || "",
    alt: media.alt || "",
    credit: media.credit || "",
    photographer: media.photographer || "",
    organization: media.organization || "",
    license: media.license || "",
    rights_status: media.rights_status || "UNKNOWN",
    order: media.order ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const api = require("@/lib/api").default;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/memory-books/medias/${media.id}`, form);
      require("sonner").toast.success("Métadonnées mises à jour.");
      onSaved(data);
    } catch (e) {
      require("sonner").toast.error(e?.response?.data?.detail || "Échec édition");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Éditer les métadonnées</DialogTitle>
          <DialogDescription>
            Ces informations apparaîtront dans la section « Crédits photographiques » du livret.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs">Légende</Label>
            <Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ordre</Label>
            <Input type="number" value={form.order}
              onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
          </div>
          <div>
            <Label className="text-xs">Photographe</Label>
            <Input value={form.photographer} onChange={(e) => setForm({ ...form, photographer: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Organisme / Média</Label>
            <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Crédit à afficher</Label>
            <Input value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })}
              placeholder="© Jean Dupont / Tahiti Sport Presse" />
          </div>
          <div>
            <Label className="text-xs">Licence</Label>
            <Input value={form.license} onChange={(e) => setForm({ ...form, license: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Statut des droits</Label>
            <Select value={form.rights_status} onValueChange={(v) => setForm({ ...form, rights_status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(RIGHTS_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Texte alternatif (accessibilité)</Label>
            <Input value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
